import React, { useEffect, useRef, useState } from 'react'
import * as pdfjs from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.entry'

interface PdfPopupRendererProps {
  pdfUrl: string
  isOpen: boolean
  onClose: () => void
  scale?: number
}

const PdfPopupRenderer: React.FC<PdfPopupRendererProps> = ({
  pdfUrl,
  isOpen,
  onClose,
  scale = 0.5 // Set default scale to 50%
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const canvasRefs = useRef<HTMLCanvasElement[]>([])
  const [currentScale, setCurrentScale] = useState(scale)

  // const handleZoomIn = () => setCurrentScale(prev => Math.min(prev + 0.1, 0.6))
  // const handleZoomOut = () => setCurrentScale(prev => Math.max(prev - 0.1, 0.5))
  const handleResetZoom = () => setCurrentScale(scale)

  useEffect(() => {
    const renderPDF = async () => {
      if (!isOpen) return

      try {
        canvasRefs.current.forEach(canvas => {
          canvas.remove()
        })
        canvasRefs.current = []

        const loadingTask = pdfjs.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        setNumPages(pdf.numPages)

        // Get device pixel ratio
        const pixelRatio = window.devicePixelRatio || 1

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum)

          // Create viewport with higher DPI
          const viewport = page.getViewport({
            scale: currentScale * pixelRatio
          })

          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d', { alpha: false })

          if (!context || !containerRef.current) continue

          // Set canvas size to actual pixels
          canvas.width = viewport.width
          canvas.height = viewport.height

          // Set display size
          canvas.style.width = `${viewport.width / pixelRatio}px`
          canvas.style.height = `${viewport.height / pixelRatio}px`

          // Configure high-quality rendering
          context.imageSmoothingEnabled = true
          context.imageSmoothingQuality = 'high'

          const renderContext = {
            canvasContext: context,
            viewport: viewport,
            enableWebGL: true,
            renderInteractiveForms: true
          }

          await page.render(renderContext).promise
          canvas.className = 'mb-4 border border-gray-300 shadow-md'
          containerRef.current.appendChild(canvas)
          canvasRefs.current.push(canvas)
        }
      } catch (error) {
        console.error('Error rendering PDF:', error)
      }
    }

    renderPDF()

    return () => {
      canvasRefs.current.forEach(canvas => {
        canvas.remove()
      })
      canvasRefs.current = []
    }
  }, [pdfUrl, currentScale, isOpen])

  if (!isOpen) return null

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='relative bg-white rounded-lg shadow-xl w-[96vw] h-[85vh] max-w-4xl '>
        <div className='flex justify-between items-center p-3 border-b'>
          <div className='flex items-center space-x-2'>
          </div>
          <button
            onClick={onClose}
            className='p-1 hover:bg-gray-100 rounded-full'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* PDF Content */}
        <div className='w-full h-[calc(85vh-88px)] overflow-auto p-3'>
          <div ref={containerRef} className='flex flex-col items-center'>
            {numPages === 0 && (
              <div className='flex items-center justify-center w-full h-full'>
                <p className='text-gray-500'>Loading PDF...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PdfPopupRenderer
